'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { NotificationContainer, NotificationData, NotificationType } from '@/components/Notification';

interface NotificationContextValue {
    success: (message: string, description?: string, duration?: number) => void;
    error: (message: string, description?: string, duration?: number) => void;
    warning: (message: string, description?: string, duration?: number) => void;
    info: (message: string, description?: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function useNotification() {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within NotificationProvider');
    }
    return context;
}

interface NotificationProviderProps {
    children: React.ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
    const [notifications, setNotifications] = useState<NotificationData[]>([]);

    const addNotification = useCallback(
        (type: NotificationType, message: string, description?: string, duration?: number) => {
            const id = Math.random().toString(36).substring(2, 9);
            const notification: NotificationData = {
                id,
                type,
                message,
                description,
                duration: duration || 4000,
            };

            setNotifications((prev) => [...prev, notification]);
        },
        []
    );

    const dismissNotification = useCallback((id: string) => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, []);

    const value: NotificationContextValue = {
        success: useCallback(
            (message: string, description?: string, duration?: number) =>
                addNotification('success', message, description, duration),
            [addNotification]
        ),
        error: useCallback(
            (message: string, description?: string, duration?: number) =>
                addNotification('error', message, description, duration),
            [addNotification]
        ),
        warning: useCallback(
            (message: string, description?: string, duration?: number) =>
                addNotification('warning', message, description, duration),
            [addNotification]
        ),
        info: useCallback(
            (message: string, description?: string, duration?: number) =>
                addNotification('info', message, description, duration),
            [addNotification]
        ),
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <NotificationContainer notifications={notifications} onDismiss={dismissNotification} />
        </NotificationContext.Provider>
    );
}
